import React, { memo, useMemo } from "react";
import { round, isNull } from "lodash";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import usePermissions from "../../../hooks/usePermissions";
import { validPogItem } from "../../../utils/itemValidator";
import { SCOPES } from "../../../permissions/permissions-map";
import ReleaseStatusChip from "../../ManagePlanograms/ReleaseStatusChip";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomInput from "../../../components/CustomInput";
import NumericLabel from "../../../components/NumericLabel";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  showNotifier,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../../store/globalSlice";
import {
  singleValue,
  isAdjQtyValid,
  prettyNumericRound,
  getFormattedNumber,
  orderStatus,
  getIndicators,
  isDropShipItem,
} from "../../../utils";
import { isItemDepartment } from "../../../utils/deptValidator";
import itemMappings from "./itemMappings";
import SSItemLoader from "./inlineLoader";

let timer, correctValueTimer;
const AutoStepTime = 150;
const AutoInputUpperLimit = 9999;
const AutoProps = (adjQtyError) => ({
  min: 0,
  max: 9999,
  style: { backgroundColor: adjQtyError ? "rgba(255, 0, 0, 0.7)" : "" },
});

const SSItemRowSx = (
  loading = true,
  isSelected = false,
  isRestricted = false,
  isReleased = false
) => ({
  fontSize: "0.75rem",
  marginTop: "2px",
  backgroundColor: !loading && isReleased ? "#00000014" : "",
  boxShadow: !loading
    ? getIndicators(
        false,
        isRestricted,
        isSelected,
        isReleased,
        false,
        false,
        false
      )
    : "",

  "& .ss-item-title": {
    fontWeight: 600,
  },
});

const SSItemSaveIconSx = {
  color: "primary.main",
};

const SSItemSaveBtn = {
  padding: 0,
  "&.Mui-disabled": {
    opacity: 0.4,
  },
};

const SSTruncateText = {
  display: "inline",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const SSPogOrderRow = {
  ml: 5.5,
  height: "24px",
  alignItems: "center",
  marginBottom: "3px",

  "& .truncated-text": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const InlineLabelStyle = {
  fontWeight: 500,
};

function SSItem({
  item = {},
  handleProcessData = () => {},
  handleInlineSave = () => {},
  pogFreezed = false,
  pogAvailable = false,
}) {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const store = useSelector((store) => store.global.store);
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const ssPogItemSummary = useSelector(
    (store) => store.global.ssPogItemSummary
  );

  const autoInputProps = AutoProps(
    adjQtyError === item?._id ? adjQtyError : null
  );

  const disabledAllFields =
    store?.pending ||
    store?.isArchived ||
    store?.isCompleted ||
    store?.dataguideInProgress ||
    !hasPermission({ scopes: [SCOPES.canEdit] }) ||
    !validPogItem(item) ||
    pogFreezed ||
    !pogAvailable ||
    ssPogItemSummary?.Selected === singleValue.no;

  const selectedNo = item?.SelectedItem !== singleValue.yes;

  const isPartOfItemsDept = isItemDepartment(item);

  const isRestricted = useMemo(
    () => item?.Restricted === singleValue.yes,
    [item?.Restricted]
  );

  const isReleased = useMemo(
    () => item?.ReleaseStatus === orderStatus.released,
    [item?.ReleaseStatus]
  );

  const handleChange = useStableCallback((field) => (event) => {
    let value = "";
    switch (field) {
      case "selection":
        value = event?.target?.checked;
        break;
      case "IsFree":
        value = event?.target?.checked ? 1 : 0;
        break;
      default:
        value = event?.target?.value;
    }
    handleProcessData({ field, value });
  });

  const handleShowAdjQtyError = useStableCallback((number = 0) => {
    timer = setTimeout(() => {
      dispatch(
        showNotifier({
          severity: "error",
          message: isAdjQtyValid(number)
            ? "Invalid quantity value"
            : `Quantity value of ${number} is not allowed`,
        })
      );
      dispatch(setAdjQtyError(item?._id));
    }, AutoStepTime);
  });

  const handleAdjQtyChange = (event) => {
    let newValue = event?.target?.value;
    clearTimeout(timer);
    clearTimeout(correctValueTimer);
    if (!prevAdjQty) {
      dispatch(setPrevAdjQty(item?.AdjQty));
    }
    if (adjQtyError) {
      dispatch(setAdjQtyError(null));
    }
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    newValue = currentValue;
    if (currentValue > AutoInputUpperLimit || isNaN(currentValue)) {
      return event.preventDefault();
    }
    handleProcessData({ field: "AdjQty", value: newValue });
    if (!isAdjQtyValid(newValue, isPartOfItemsDept)) {
      handleShowAdjQtyError(newValue);
    }
    return true;
  };

  const handleSave = useStableCallback(() => {
    handleInlineSave(item);
  });

  return (
    <Grid
      container
      sx={SSItemRowSx(
        item?.virtualLoading,
        item?.selection || item?.SelectedItem === singleValue.yes,
        isRestricted,
        isReleased
      )}
    >
      {!item?.virtualLoading ? (
        <>
          <Grid
            container
            columnGap={0.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={0.15}>
              <Checkbox
                size="small"
                disableRipple
                checked={item?.selection}
                onClick={handleChange("selection")}
                disabled={disabledAllFields || adjQtyError}
              />
            </Grid>
            <Grid item xs={2.2} sx={SSTruncateText}>
              <Tooltip title={item?.ItemDescription}>
                <span className="ss-item-title">{item?.ItemDescription}</span>
              </Tooltip>
            </Grid>
            <Grid item xs={0.45}>
              <span>{item?.StorePlanogramId}</span>
            </Grid>
            <Grid item xs={0.3}>
              <span>{item?.SKU}</span>
            </Grid>
            <Grid item xs={0.5} sx={SSTruncateText}>
              <Tooltip
                placement="top"
                title={!!item?.IsFree ? "Free" : "Non Free"}
              >
                <Switch
                  checked={!!item?.IsFree}
                  size="small"
                  onClick={handleChange("IsFree")}
                  disabled={disabledAllFields || adjQtyError}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={0.3}>
              <span>{Boolean(item?.OSOSelected) ? "Yes" : "No"}</span>
            </Grid>
            <Grid item xs={0.3}>
              <CustomDropdown
                value={item?.SelectedItem}
                inputsx={{ fontSize: "10px" }}
                onChange={handleChange("SelectedItem")}
                disabled={disabledAllFields || adjQtyError || isPartOfItemsDept}
              >
                <option value="N">N</option>
                <option value="Y">Y</option>
              </CustomDropdown>
            </Grid>
            <Grid item xs={0.4}>
              {isPartOfItemsDept ? (
                <>&nbsp;&nbsp;-</>
              ) : (
                <NumericLabel isSeparator value={round(item?.RecQty || 0, 2)} />
              )}
            </Grid>
            <Grid item xs={0.2}>
              {!!item?.BreakPackType ? item?.BreakPackType : "-"}
            </Grid>
            <Grid item xs={0.8}>
              <span>
                $
                {getFormattedNumber(
                  prettyNumericRound(item?.EachItemCost || 0)
                )}
              </span>
            </Grid>
            <Grid item xs={0.8}>
              <span>
                ${getFormattedNumber(prettyNumericRound(item?.AcqCost || 0))}
              </span>
            </Grid>
            <Grid item xs={0.5}>
              <CustomInput
                sx={{ width: "35px" }}
                value={!!item?.AdjQty ? item.AdjQty : 0}
                inputProps={autoInputProps}
                onChange={handleAdjQtyChange}
                disabled={
                  disabledAllFields ||
                  (adjQtyError && item?._id !== adjQtyError) ||
                  selectedNo
                }
              />
            </Grid>
            <Grid item xs={0.8}>
              <span>
                {isDropShipItem(item)
                  ? "-"
                  : `$${getFormattedNumber(
                      prettyNumericRound(item?.AdjCost || 0)
                    )}`}
              </span>
            </Grid>
            <Grid item xs={0.8}>
              <span>{item?.Type !== -1 ? itemMappings[item?.Type] : "-"}</span>
            </Grid>
            <Grid item xs={0.4}>
              <Tooltip title="Save" arrow>
                <IconButton
                  onClick={handleSave}
                  sx={SSItemSaveBtn}
                  disabled={disabledAllFields || adjQtyError}
                >
                  <SaveOutlinedIcon size="small" sx={SSItemSaveIconSx} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid
            container
            columnGap={0.5}
            alignItems="center"
            justifyContent="flex-start"
            sx={SSPogOrderRow}
          >
            <Grid item xs={1.5} className="truncated-text">
              <span style={InlineLabelStyle}>Vendor:</span>{" "}
              <Tooltip
                title={!!item?.VendorName ? item?.VendorName : "-"}
                arrow
              >
                <span>{!!item?.VendorName ? item?.VendorName : "-"}</span>
              </Tooltip>
            </Grid>
            <Grid item xs={1.5}>
              <span style={InlineLabelStyle}>PO Number:</span>{" "}
              <span>{!!item?.PONumber ? item?.PONumber : "-"}</span>
            </Grid>
            <Grid item xs={1.5}>
              <span style={InlineLabelStyle}>Release Date:</span>{" "}
              <span>
                {!!item?.ReleaseDate
                  ? dayjs(item?.ReleaseDate).format("MM/DD/YYYY")
                  : "-"}
              </span>
            </Grid>
            <Grid item xs={2}>
              <span style={InlineLabelStyle}>Release Status:</span>{" "}
              <span>
                {!!item?.ReleaseStatus ? (
                  <ReleaseStatusChip
                    status={item?.ReleaseStatus === orderStatus.released}
                  />
                ) : (
                  "-"
                )}
              </span>
            </Grid>
            <Grid item xs={1.5}>
              <span style={InlineLabelStyle}>Restricted:</span>{" "}
              <span>{item?.Restricted === singleValue.yes ? "Yes" : "No"}</span>
            </Grid>
          </Grid>
        </>
      ) : (
        <SSItemLoader />
      )}
    </Grid>
  );
}

export default memo(SSItem);
